import {
  aggregateByLine,
  aggregatePeriods,
  kmdDueFlow,
  payrollTax,
  sumPeriods,
  summarizeWindow,
  type CashFlowEntry as CoreEntry,
  type DayRollup as CoreDay,
  type LineRollup,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { formatEur, type CashFlowEntry, type DayRollup } from '@/lib/cashFlow';
import { LINE_META, INCOME_CATS, COST_CATS } from './categoryMeta';

/**
 * "Millest „kokku" koosneb" — the composition modal of the primary view. It
 * explains how the window's bank balance moves from algsaldo to lõppsaldo: each
 * named line, the income/cost subtotals, the net cash flow, and the KMD/TSD that
 * sit inside the costs. Like the KPI strip, it is about REAL cash, so every figure
 * is GROSS and ignores the NET/GROSS toggle.
 *
 * It never sums money itself (L9 / master rule): line totals come from
 * `aggregateByLine`, the headline figures from `summarizeWindow`, KMD from
 * `kmdDueFlow` (the declaration entries DUE in the window — the same
 * materialized rows the Maksud line carries, consistent with the table lens's
 * KMD row), TSD from `payrollTax`. The component only SELECTS gross fields.
 */
export interface CompositionProps {
  days: DayRollup[];
  entries: CashFlowEntry[];
  today: string;
  openingBalance: number;
  onClose: () => void;
}

function Line({
  label,
  value,
  tone,
  strong,
  info,
}: {
  label: string;
  value: number;
  tone: 'income' | 'cost' | 'neutral';
  strong?: boolean;
  info?: boolean;
}) {
  const color =
    tone === 'income'
      ? 'text-[hsl(var(--income))]'
      : tone === 'cost'
        ? 'text-[hsl(var(--cost))]'
        : 'text-foreground';
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 px-3 py-1.5 text-xs',
        strong && 'border-t border-border bg-card/60 font-semibold',
        info && 'text-muted-foreground',
      )}
    >
      <span className={strong ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
      <span className={cn('tabular-nums', color, strong && 'font-semibold')}>{formatEur(value)}</span>
    </div>
  );
}

export function Composition({ days, entries, today, openingBalance, onClose }: CompositionProps) {
  const from = days[0].date;
  const to = days[days.length - 1].date;
  const lines = aggregateByLine(entries as CoreEntry[], { today, from, to });
  const periods = aggregatePeriods(days as CoreDay[], 'month');
  const closingBalance = sumPeriods(periods).closingBalance;
  const summary = summarizeWindow(lines, closingBalance);
  const kmd = kmdDueFlow(entries as CoreEntry[], { today, from, to });
  const tsd = payrollTax(entries as CoreEntry[], { today, from, to });

  const byCat = new Map<string, LineRollup>(lines.map((l) => [l.category, l]));
  const income = INCOME_CATS.map((c) => byCat.get(c)!);
  const cost = COST_CATS.map((c) => byCat.get(c)!);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Summa koosseis"
      className="absolute inset-0 z-50 flex items-start justify-center bg-background/70 p-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold text-foreground">Millest „kokku“ koosneb</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sulge"
            className="rounded px-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            ✕
          </button>
        </div>
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
          Periood: algsaldo → lõppsaldo · bruto raha
        </div>
        <Line label="Algsaldo (pangakontol)" value={openingBalance} tone="neutral" />
        <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Laekumised
        </div>
        {income.map((l) => (
          <Line key={l.category} label={LINE_META[l.category].label} value={l.income.total.gross} tone="income" />
        ))}
        <Line label="Tulu kokku" value={summary.sales} tone="income" strong />
        <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Väljaminekud
        </div>
        {cost.map((l) => (
          <Line key={l.category} label={LINE_META[l.category].label} value={l.cost.total.gross} tone="cost" />
        ))}
        <Line label="Kulu kokku" value={summary.totalCost} tone="cost" strong />
        <Line label="= Rahavoog" value={summary.margin} tone="neutral" strong />
        <Line label="= Lõppsaldo (pangakontol)" value={summary.closingBalance} tone="neutral" strong />
        <div className="border-t border-border px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Maksud (sisaldub väljaminekutes)
        </div>
        <Line label="KMD — käibemaks · tasu 20. kpv" value={kmd.gross} tone="cost" info />
        <Line label="TSD — palga- ja tulumaks · tasu 10. kpv" value={tsd.gross} tone="cost" info />
        <p className="px-3 py-2 text-[10px] leading-relaxed text-muted-foreground">
          KMD = perioodil tasumisele kuuluvad käibedeklaratsioonid (eelmise kuu
          dokumentide käibemaks); TSD = palgamaksud. Summad arvutab finance-core
          (sentides), komponent ei arvuta.
        </p>
      </div>
    </div>
  );
}
